import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Building2, Store, MapPin, Euro, Star, Filter, ArrowUpRight, ArrowDownRight, BarChart3, Maximize2 } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Legend, Cell } from 'recharts';
// Level options
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'markt',
  name: 'Autohäuser',
  icon: Store
}];
const countries = [{
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'usa',
  name: 'USA'
}, {
  id: 'china',
  name: 'China'
}, {
  id: 'uk',
  name: 'Großbritannien'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'australien',
  name: 'Australien'
}, {
  id: 'japan',
  name: 'Japan'
}];
// Autohaus data with revenue, rating, and showroom size
const marketsData = [{
  id: 'hamburg-niederlassung',
  name: 'BMW Niederlassung Hamburg',
  region: 'Deutschland',
  rating: 4.3,
  revenue: 42000,
  size: 2800,
  revenuePerSqm: 15000
}, {
  id: 'berlin-welzel',
  name: 'Autohaus Welzel Berlin-Kreuzberg',
  region: 'Deutschland',
  rating: 3.8,
  revenue: 17500,
  size: 1200,
  revenuePerSqm: 14583
}, {
  id: 'muenchen-niederlassung',
  name: 'BMW Niederlassung München',
  region: 'Deutschland',
  rating: 4.6,
  revenue: 32000,
  size: 1800,
  revenuePerSqm: 17778
}, {
  id: 'nuernberg-niederlassung',
  name: 'BMW Niederlassung Nürnberg',
  region: 'Deutschland',
  rating: 4.3,
  revenue: 26000,
  size: 1700,
  revenuePerSqm: 15294
}, {
  id: 'dresden-niederlassung',
  name: 'BMW Niederlassung Dresden',
  region: 'Deutschland',
  rating: 4.1,
  revenue: 19000,
  size: 1250,
  revenuePerSqm: 15200
}, {
  id: 'stuttgart-niederlassung',
  name: 'BMW Niederlassung Stuttgart',
  region: 'Deutschland',
  rating: 4.4,
  revenue: 28500,
  size: 1650,
  revenuePerSqm: 17273
}, {
  id: 'frankfurt-niederlassung',
  name: 'BMW Niederlassung Frankfurt',
  region: 'Deutschland',
  rating: 4.2,
  revenue: 24000,
  size: 1500,
  revenuePerSqm: 16000
}, {
  id: 'manhattan-bmw',
  name: 'BMW of Manhattan, New York',
  region: 'USA',
  rating: 3.5,
  revenue: 28500,
  size: 1100,
  revenuePerSqm: 25909
}, {
  id: 'beverly-hills-bmw',
  name: 'BMW of Beverly Hills',
  region: 'USA',
  rating: 4.4,
  revenue: 38000,
  size: 1600,
  revenuePerSqm: 23750
}, {
  id: 'chicago-bmw',
  name: 'BMW of Chicago',
  region: 'USA',
  rating: 3.7,
  revenue: 22000,
  size: 1050,
  revenuePerSqm: 20952
}, {
  id: 'dallas-bmw',
  name: 'BMW of Dallas',
  region: 'USA',
  rating: 4.1,
  revenue: 31000,
  size: 1400,
  revenuePerSqm: 22143
}, {
  id: 'miami-bmw',
  name: 'BMW of Miami',
  region: 'USA',
  rating: 4.3,
  revenue: 34000,
  size: 1500,
  revenuePerSqm: 22667
}, {
  id: 'beijing-bmw',
  name: 'BMW Center Beijing',
  region: 'China',
  rating: 3.9,
  revenue: 31000,
  size: 1400,
  revenuePerSqm: 22143
}, {
  id: 'shenyang-bmw',
  name: 'BMW Brilliance Shenyang',
  region: 'China',
  rating: 4.5,
  revenue: 48000,
  size: 2200,
  revenuePerSqm: 21818
}, {
  id: 'shanghai-bmw',
  name: "BMW Shanghai Jing'an",
  region: 'China',
  rating: 3.6,
  revenue: 26000,
  size: 1300,
  revenuePerSqm: 20000
}, {
  id: 'guangzhou-bmw',
  name: 'BMW Guangzhou Tianhe',
  region: 'China',
  rating: 4.0,
  revenue: 29000,
  size: 1350,
  revenuePerSqm: 21481
}, {
  id: 'chengdu-bmw',
  name: 'BMW Chengdu Jinniu',
  region: 'China',
  rating: 4.2,
  revenue: 33000,
  size: 1550,
  revenuePerSqm: 21290
}, {
  id: 'london-park-lane',
  name: 'BMW Park Lane, London',
  region: 'Großbritannien',
  rating: 4.2,
  revenue: 35000,
  size: 1500,
  revenuePerSqm: 23333
}, {
  id: 'manchester-bmw',
  name: 'BMW Manchester Deansgate',
  region: 'Großbritannien',
  rating: 3.8,
  revenue: 22000,
  size: 1100,
  revenuePerSqm: 20000
}, {
  id: 'birmingham-bmw',
  name: 'BMW Birmingham Broad Street',
  region: 'Großbritannien',
  rating: 4.0,
  revenue: 24500,
  size: 1200,
  revenuePerSqm: 20417
}, {
  id: 'edinburgh-bmw',
  name: 'BMW Edinburgh',
  region: 'Großbritannien',
  rating: 4.3,
  revenue: 19000,
  size: 950,
  revenuePerSqm: 20000
}, {
  id: 'paris-champs',
  name: 'BMW Paris Champs-Élysées',
  region: 'Frankreich',
  rating: 4.1,
  revenue: 29000,
  size: 1350,
  revenuePerSqm: 21481
}, {
  id: 'lyon-bmw',
  name: 'BMW Lyon Part-Dieu',
  region: 'Frankreich',
  rating: 3.9,
  revenue: 18500,
  size: 1000,
  revenuePerSqm: 18500
}, {
  id: 'marseille-bmw',
  name: 'BMW Marseille Prado',
  region: 'Frankreich',
  rating: 3.7,
  revenue: 16000,
  size: 950,
  revenuePerSqm: 16842
}, {
  id: 'bordeaux-bmw',
  name: 'BMW Bordeaux Mériadeck',
  region: 'Frankreich',
  rating: 4.2,
  revenue: 17500,
  size: 1000,
  revenuePerSqm: 17500
}, {
  id: 'sydney-bmw',
  name: 'BMW Sydney Rushcutters Bay',
  region: 'Australien',
  rating: 4.4,
  revenue: 27000,
  size: 1650,
  revenuePerSqm: 16364
}, {
  id: 'melbourne-bmw',
  name: 'BMW Melbourne Collins Street',
  region: 'Australien',
  rating: 4.2,
  revenue: 22000,
  size: 1550,
  revenuePerSqm: 14194
}, {
  id: 'brisbane-bmw',
  name: 'BMW Brisbane Fortitude Valley',
  region: 'Australien',
  rating: 4.0,
  revenue: 18500,
  size: 1200,
  revenuePerSqm: 15417
}, {
  id: 'perth-bmw',
  name: 'BMW Perth Osborne Park',
  region: 'Australien',
  rating: 3.8,
  revenue: 15000,
  size: 1050,
  revenuePerSqm: 14286
}, {
  id: 'tokyo-bmw',
  name: 'BMW Tokyo Aoyama',
  region: 'Japan',
  rating: 4.0,
  revenue: 24000,
  size: 1300,
  revenuePerSqm: 18462
}, {
  id: 'osaka-bmw',
  name: 'BMW Osaka Midosuji',
  region: 'Japan',
  rating: 3.9,
  revenue: 18000,
  size: 1150,
  revenuePerSqm: 15652
}, {
  id: 'nagoya-bmw',
  name: 'BMW Nagoya Sakae',
  region: 'Japan',
  rating: 4.1,
  revenue: 20000,
  size: 1200,
  revenuePerSqm: 16667
}, {
  id: 'fukuoka-bmw',
  name: 'BMW Fukuoka Tenjin',
  region: 'Japan',
  rating: 3.7,
  revenue: 15500,
  size: 1000,
  revenuePerSqm: 15500
}];
// Land aggregated data
const regionsData = [{
  id: 'deutschland',
  name: 'Deutschland',
  rating: 4.2,
  revenue: 136500,
  size: 8750,
  revenuePerSqm: 15600,
  markets: 5
}, {
  id: 'usa',
  name: 'USA',
  rating: 3.95,
  revenue: 66500,
  size: 2700,
  revenuePerSqm: 24630,
  markets: 2
}, {
  id: 'china',
  name: 'China',
  rating: 4.2,
  revenue: 79000,
  size: 3600,
  revenuePerSqm: 21944,
  markets: 2
}, {
  id: 'uk',
  name: 'Großbritannien',
  rating: 4.2,
  revenue: 35000,
  size: 1500,
  revenuePerSqm: 23333,
  markets: 1
}, {
  id: 'frankreich',
  name: 'Frankreich',
  rating: 4.1,
  revenue: 29000,
  size: 1350,
  revenuePerSqm: 21481,
  markets: 1
}, {
  id: 'australien',
  name: 'Australien',
  rating: 4.3,
  revenue: 49000,
  size: 3200,
  revenuePerSqm: 15313,
  markets: 2
}, {
  id: 'japan',
  name: 'Japan',
  rating: 3.95,
  revenue: 42000,
  size: 2450,
  revenuePerSqm: 17143,
  markets: 2
}];
// Konzern data
const konzernData = {
  name: 'BMW Gesamtnetz Weltweit',
  rating: 4.15,
  revenue: 437000,
  size: 23550,
  revenuePerSqm: 18556,
  markets: 15,
  regions: 7
};
// Time series data (monatlicher Gesamtumsatz T€ über alle Niederlassungen)
const timeSeriesData = [{
  month: 'Feb 23',
  rating: 4.05,
  revenue: 318000,
  revenuePerSqm: 13504
}, {
  month: 'Mär 23',
  rating: 4.08,
  revenue: 322000,
  revenuePerSqm: 13674
}, {
  month: 'Apr 23',
  rating: 4.06,
  revenue: 319500,
  revenuePerSqm: 13568
}, {
  month: 'Mai 23',
  rating: 4.1,
  revenue: 328000,
  revenuePerSqm: 13929
}, {
  month: 'Jun 23',
  rating: 4.12,
  revenue: 334000,
  revenuePerSqm: 14184
}, {
  month: 'Jul 23',
  rating: 4.09,
  revenue: 326000,
  revenuePerSqm: 13844
}, {
  month: 'Aug 23',
  rating: 4.11,
  revenue: 330000,
  revenuePerSqm: 14014
}, {
  month: 'Sep 23',
  rating: 4.14,
  revenue: 338000,
  revenuePerSqm: 14354
}, {
  month: 'Okt 23',
  rating: 4.13,
  revenue: 340000,
  revenuePerSqm: 14439
}, {
  month: 'Nov 23',
  rating: 4.16,
  revenue: 346000,
  revenuePerSqm: 14694
}, {
  month: 'Dez 23',
  rating: 4.18,
  revenue: 351000,
  revenuePerSqm: 14906
}, {
  month: 'Jan 24',
  rating: 4.2,
  revenue: 355500,
  revenuePerSqm: 15096
}];
// Calculate correlation coefficient
const calculateCorrelation = (data: Array<{
  rating: number;
  revenuePerSqm: number;
}>) => {
  const n = data.length;
  const sumX = data.reduce((acc, d) => acc + d.rating, 0);
  const sumY = data.reduce((acc, d) => acc + d.revenuePerSqm, 0);
  const sumXY = data.reduce((acc, d) => acc + d.rating * d.revenuePerSqm, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.revenuePerSqm * d.revenuePerSqm, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
// Custom tooltip for scatter chart
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-1366">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-1367">{data.name}</div>
        <div className="space-y-1" data-id="element-1368">
          <div className="flex justify-between gap-4" data-id="element-1369">
            <span className="text-gray-500" data-id="element-1370">Rating:</span>
            <span className="font-medium" data-id="element-1371">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1372">
            <span className="text-gray-500" data-id="element-1373">Umsatz/m²:</span>
            <span className="font-medium" data-id="element-1374">
              {data.revenuePerSqm.toLocaleString('de-DE')} €
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1375">
            <span className="text-gray-500" data-id="element-1376">Gesamtumsatz:</span>
            <span className="font-medium" data-id="element-1377">
              {(data.revenue / 1000).toFixed(1)}M €
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1378">
            <span className="text-gray-500" data-id="element-1379">Fläche:</span>
            <span className="font-medium" data-id="element-1380">
              {data.size.toLocaleString('de-DE')} m²
            </span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function StrategicAdvisorPage() {
  const [selectedLevel, setSelectedLevel] = useState<string>('konzern');
  const getScatterData = () => {
    if (selectedLevel === 'konzern') return marketsData.map(m => ({
      ...m,
      z: m.size
    }));
    if (selectedLevel === 'markt') return marketsData.map(m => ({
      ...m,
      z: m.size
    }));
    const countryName = countries.find(c => c.id === selectedLevel)?.name;
    return marketsData.filter(m => m.region === countryName).map(m => ({
      ...m,
      z: m.size
    }));
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  // Find top and bottom performers
  const sortedByEfficiency = [...scatterData].sort((a, b) => b.revenuePerSqm - a.revenuePerSqm);
  const topPerformers = sortedByEfficiency.slice(0, 3);
  const bottomPerformers = sortedByEfficiency.slice(-3).reverse();
  // Calculate potential
  const avgRevenuePerSqm = scatterData.reduce((acc, d) => acc + d.revenuePerSqm, 0) / scatterData.length;
  const underperformers = scatterData.filter(d => d.revenuePerSqm < avgRevenuePerSqm);
  const avgGapPct = underperformers.length > 0 ? underperformers.reduce((acc, d) => acc + (avgRevenuePerSqm - d.revenuePerSqm) / avgRevenuePerSqm * 100, 0) / underperformers.length : 0;
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1381">
      {/* Level Filter */}
      <Card className="!p-4" data-id="element-1382">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1383">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1384">
            <Filter size={16} data-id="element-1385" />
            <span data-id="element-1386">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1387">
            <button onClick={() => setSelectedLevel('konzern')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${selectedLevel === 'konzern' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedLevel === 'konzern' ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-1388">
              <Building2 size={14} data-id="element-1389" />
              Konzern-Übersicht
            </button>

            <div className="flex items-center" data-id="element-1390">
              <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-1391" />
            </div>

            {countries.map(country => <button key={country.id} onClick={() => setSelectedLevel(country.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedLevel === country.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedLevel === country.id ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-1392">
                {country.name}
              </button>)}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1393">
        <KPICard title="Korrelation Rating ↔ Umsatz" value={correlation.toFixed(2)} change={correlation > 0.7 ? 'Stark positiv' : correlation > 0.4 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1395" />} subtext="Pearson-Koeffizient" data-id="element-1394" />
        <KPICard title="Ø Umsatz pro m² Showroom" value={`${avgRevenuePerSqm.toLocaleString('de-DE', {
        maximumFractionDigits: 0
      })} €`} change="+4.2%" trend="up" icon={<Euro size={18} data-id="element-1397" />} subtext="vs. Vorjahr" data-id="element-1396" />
        <KPICard title="Ø Bewertung" value={(scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length).toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1399" />} subtext="Google Rating" data-id="element-1398" />
    
      </div>

      {/* Main Scatter Chart */}
      <Card data-id="element-1400">
        <div className="p-6" data-id="element-1401">
          <div className="flex items-center justify-between mb-6" data-id="element-1402">
            <div data-id="element-1403">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1404">
                Rating vs. Umsatzeffizienz
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1405">
                Blasengröße = Showroomfläche (m²) •{' '}
                {selectedLevel === 'markt' ? 'Autohäuser' : selectedLevel === 'konzern' ? 'Konzern' : countries.find(c => c.id === selectedLevel)?.name}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-1406">
              <div className="flex items-center gap-1" data-id="element-1407">
                <div className="w-3 h-3 rounded-full opacity-30" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-1408"></div>
                <span data-id="element-1409">Klein</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1410">
                <div className="w-4 h-4 rounded-full opacity-50" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-1411"></div>
                <span data-id="element-1412">Mittel</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1413">
                <div className="w-5 h-5 rounded-full opacity-70" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-1414"></div>
                <span data-id="element-1415">Groß</span>
              </div>
            </div>
          </div>

          <div className="h-96" data-id="element-1416">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1417">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1418">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1419" />
                <XAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1420" />
                <YAxis type="number" dataKey="revenuePerSqm" name="Umsatz/m²" domain={[6000, 19000]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => `${(value / 1000).toFixed(0)}k`} label={{
                value: 'Umsatz pro m² Showroom (€)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1421" />
                <ZAxis type="number" dataKey="z" range={[100, 1000]} data-id="element-1422" />
                <Tooltip content={<CustomScatterTooltip data-id="element-1424" />} data-id="element-1423" />
                <Scatter name="Autohäuser" data={scatterData} fill="#1C69D4" data-id="element-1425">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.rating >= 4.3 ? '#639754' : entry.rating >= 3.8 ? '#1C69D4' : '#E03C32'} fillOpacity={0.7} data-id="element-1426" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-1427">
            <div className="flex items-center gap-2" data-id="element-1428">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-1429" />
              <span className="text-gray-600" data-id="element-1430">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#F59E0B'
              }} data-id="element-1431">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Höhere Bewertungen korrelieren mit höherem Umsatz pro m²
              </span>
            </div>
            <div className="text-gray-500" data-id="element-1432">
              {scatterData.length}{' '}
              {selectedLevel === 'markt' ? 'Autohäuser' : selectedLevel === 'konzern' ? 'Datenpunkt' : 'Autohäuser'}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1433">
        {/* Top Performers */}
        <Card data-id="element-1434">
          <div className="p-6" data-id="element-1435">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1436">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-1437" />
              Top Performer
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1438">
              Höchste Umsatzeffizienz (€/m² Showroom)
            </p>
            <div className="space-y-3" data-id="element-1439">
              {topPerformers.map((item, index) => <motion.div key={item.id || item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1440">
                  <div className="flex items-center gap-3" data-id="element-1441">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1442">
                      {index + 1}
                    </div>
                    <div data-id="element-1443">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-1444">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1445">
                        {item.rating.toFixed(1)} ★ •{' '}
                        {item.size.toLocaleString('de-DE')} m²
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1446">
                    <div className="font-bold text-lg" style={{
                  color: '#639754'
                }} data-id="element-1447">
                      {item.revenuePerSqm.toLocaleString('de-DE')} €
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1448">pro m²</div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Card>

        {/* Bottom Performers */}
        <Card data-id="element-1449">
          <div className="p-6" data-id="element-1450">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1451">
              <ArrowDownRight className="text-red-600" size={20} data-id="element-1452" />
              Optimierungspotenzial
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1453">
              Niedrigste Umsatzeffizienz (€/m² Showroom)
            </p>
            <div className="space-y-3" data-id="element-1454">
              {bottomPerformers.map((item, index) => <motion.div key={item.id || item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-1455">
                  <div className="flex items-center gap-3" data-id="element-1456">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-1457">
                      {scatterData.length - index}
                    </div>
                    <div data-id="element-1458">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-1459">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1460">
                        {item.rating.toFixed(1)} ★ •{' '}
                        {item.size.toLocaleString('de-DE')} m²
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1461">
                    <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-1462">
                      {item.revenuePerSqm.toLocaleString('de-DE')} €
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1463">
                      pro m² (
                      {((avgRevenuePerSqm - item.revenuePerSqm) / avgRevenuePerSqm * 100).toFixed(0)}
                      % unter Ø)
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Card>
      </div>

      {/* Time Series Chart */}
      <Card data-id="element-1464">
        <div className="p-6" data-id="element-1465">
          <div className="flex items-center justify-between mb-6" data-id="element-1466">
            <div data-id="element-1467">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1468">
                Entwicklung über Zeit
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1469">
                Gesamtnetz-Umsatz und Durchschnittsbewertung (12 Monate)
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1470">
              <div className="flex items-center gap-2" data-id="element-1471">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-1472"></div>
                <span className="text-gray-600" data-id="element-1473">Umsatz (Mio. €)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1474">
                <div className="w-6 h-0.5" style={{
                backgroundColor: '#F59E0B'
              }} data-id="element-1475"></div>
                <span className="text-gray-600" data-id="element-1476">Rating</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-1477">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1478">
              <ComposedChart data={timeSeriesData} data-id="element-1479">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1480" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1481" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[300000, 370000]} tickFormatter={value => `${(value / 1000).toFixed(0)}`} label={{
                value: 'Umsatz (Mio. €)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1482" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.9, 4.4]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1483" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'revenue' ? `${(value / 1000).toFixed(1)} Mio. €` : value.toFixed(2), name === 'revenue' ? 'Umsatz' : 'Rating']} data-id="element-1484" />
                <Bar yAxisId="left" dataKey="revenue" fill="#1C69D4" radius={[4, 4, 0, 0]} barSize={30} data-id="element-1485" />
                <Line yAxisId="right" type="monotone" dataKey="rating" stroke="#F59E0B" strokeWidth={3} dot={{
                fill: '#F59E0B',
                strokeWidth: 2,
                r: 4
              }} data-id="element-1486" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4 text-center" data-id="element-1487">
            <div data-id="element-1488">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1489">Umsatzwachstum</div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1490">
                +11.8%
              </div>
            </div>
            <div data-id="element-1491">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1492">
                Rating-Verbesserung
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1493">
                +0.15
              </div>
            </div>
            <div data-id="element-1494">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1495">
                Effizienzsteigerung
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1496">
                +11.8%
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Table */}
      <Card data-id="element-1497">
        <div className="p-6" data-id="element-1498">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1499">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Autohäuser' : selectedLevel === 'konzern' ? 'Konzern' : countries.find(c => c.id === selectedLevel)?.name || ''}
          </h3>
          <div className="overflow-x-auto" data-id="element-1500">
            <table className="w-full text-sm" data-id="element-1501">
              <thead data-id="element-1502">
                <tr className="border-b border-gray-200" data-id="element-1503">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1504">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1505">
                      Land
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1506">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1507">
                    Umsatz
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1508">
                    Showroom
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1509">
                    €/m²
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1510">
                    vs. Ø
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1511">
                {scatterData.map((item: any, index) => {
                const diff = (item.revenuePerSqm - avgRevenuePerSqm) / avgRevenuePerSqm * 100;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1512">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-1513">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-1514">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-1515">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1516">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1517">
                        {(item.revenue / 1000).toFixed(1)}M €
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1518">
                        {item.size.toLocaleString('de-DE')} m²
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-gray-900" data-id="element-1519">
                        {item.revenuePerSqm.toLocaleString('de-DE')} €
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1520">
                        <span className={`font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1521">
                          {diff >= 0 ? '+' : ''}
                          {diff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}